import { useState, useEffect } from "react";
import "./PatientStepStudyOrder.scss";
import { getPatientData, getPatientsInfo } from "../../../../services/digitalRPservice";
import { Icon } from "@iconify/react";
import GeneralRecipeViewCard from "../../../Recipe/Components/GeneralRecipeViewCard";
import { Button } from "primereact/button";
import { Combobox, InputTextCustom } from "medere-ui-framework";
import { defaultHealthPatientCoverage, HealthPatientCoverage, PatientRecordDTO } from "../../../../interface/interface";

interface PatientStepStudyOrderProps {
    professional: any;
    selectedPatient: any;
    setSelectedPatient: (p: any) => void;
    onValidationChange: (valid: boolean) => void;
    setActiveView: (v: number | undefined) => void;
    setSubActiveView: (v: number | undefined) => void;
    setPrevActiveView: (v: number | undefined) => void;
    setPrevSubActiveView: (v: number | undefined) => void;
    goToCreatePatient: () => void;
    onExitFlow: () => void;
    handleNext: () => void;
    handleBack: () => void;
    isStepValid: boolean; 
    selectedCoverage: HealthPatientCoverage | undefined;
    setSelectedCoverage: (c: HealthPatientCoverage | undefined) => void;
}

export default function PatientStepStudyOrder({professional, selectedPatient, setSelectedPatient, onValidationChange,
 setActiveView, setSubActiveView, setPrevActiveView, setPrevSubActiveView, goToCreatePatient, onExitFlow, handleNext, handleBack, isStepValid, selectedCoverage, setSelectedCoverage}: PatientStepStudyOrderProps) {
    const [patientInput, setPatientInput] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [resultsFound, setResultsFound] = useState("Todavía no se han ingresado datos de búsqueda");
    const [loading, setLoading] = useState(false);

    const normalizePatient = (p: PatientRecordDTO): PatientRecordDTO => {
        const ins = (p.insurances ?? []).map((hpc: any) => {
            const desc = hpc?.description ?? `${hpc?.healthentityplan?.healthentity?.commercialname ?? "-"}, ${hpc?.healthentityplan?.name ?? "-"}`;
            return { ...hpc, description: desc };
        });
        return { ...p, insurances: ins };
    };

    const favoriteCoverageOf = (p: PatientRecordDTO): HealthPatientCoverage => {
        const ins = (p.insurances ?? []) as any[];
        return ((ins.find((i) => i.primarycoverage) as any) ?? (ins[0] as any) ?? defaultHealthPatientCoverage);
    };

    useEffect(() => onValidationChange(!!selectedPatient), [selectedPatient]);

    useEffect(() => {
        if (!selectedPatient) return;
        if (!selectedCoverage) {
            setSelectedCoverage(favoriteCoverageOf(selectedPatient));
        }
    }, [selectedPatient]);

    useEffect(() => {
        if (!selectedPatient || !selectedCoverage || !selectedPatient.insurances?.length) return;

        const updated: PatientRecordDTO = {
            ...selectedPatient, insurances: selectedPatient.insurances.map((hpc: any) => ({
                ...hpc, primarycoverage: hpc.healthpatientcoverage === selectedCoverage.healthpatientcoverage,})),
        };

        setSelectedPatient(updated);
    }, [selectedCoverage?.healthpatientcoverage]);

    const handleSearch = async () => {
        if (!patientInput) {
            setResultsFound("Todavía no se han ingresado datos de búsqueda");
            return;
        }
        try {
            setLoading(true);
            const results = await getPatientsInfo(patientInput, 200, 0);
            setSearchResults(results);
            setResultsFound(results.length === 0 ? "No se han encontrado resultados" : "");
        } catch {
            setSearchResults([]);
            setResultsFound("No se han encontrado resultados");
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e: any) => {
        if (e.key === "Enter") handleSearch();
    };

    const handlePatientSelection = async (p: PatientRecordDTO) => {
        const full = await getPatientData(p.medereentity);
        const normalized = normalizePatient(full);
        setSelectedCoverage(favoriteCoverageOf(normalized));
        setSelectedPatient(normalized);
    };


    const removePatientSelection = () => {
        setSelectedPatient(null);
        setSelectedCoverage(undefined);
    };

    return (
        <>
        <div className="patient-step-study-order">
            <div className="left-column">
                <div className="professional-block">
                    <b><label>Profesional que prescribe</label></b><br />
                    <label>{professional?.fulldescription}</label>
                </div>

                <div className="field">
                    <label>Paciente</label>

                    <div className="patient-search-container" style={{ marginBottom: "30px", marginTop: "-8px" }}>
                        <InputTextCustom
                            className={`patient-input ${patientInput ? "active" : ""} border2`}
                            value={patientInput}
                            onChange={(e: any) => setPatientInput(e.target.value)}
                            onKeyDown={(e: any) => e.key === "Enter" && handleSearch()}
                            placeholder="Buscar por apellido, nombre o DNI"
                            style={{ minWidth: "300px", width: "100%" }}
                            disable={selectedPatient != null && selectedPatient?.medereentity > 0}
                        />

                        <i className={`${loading ? "pi pi-spinner pi-spin patient-search-icon" : "pi pi-search patient-search-icon btnRpContinue"} ${loading || (selectedPatient && selectedPatient?.medereentity > 0) ? "disabled" : ""}`}
                            onClick={selectedPatient && selectedPatient?.medereentity > 0 ? undefined : handleSearch}
                            style={{
                                cursor: loading || (selectedPatient && selectedPatient?.medereentity > 0) ? "not-allowed" : "pointer",
                                opacity: loading ? 0.7 : selectedPatient && selectedPatient?.medereentity > 0 ? 0.5 : 1,
                                marginTop: loading ? "-7px" : "",
                                color: loading ? "" : "white",
                                backgroundColor: loading ? "" : "#3CB8BA",
                                padding: loading ? "" : "12.8px",
                                marginRight: loading ? "" : "-7px",
                                borderRadius: "2px"
                            }}
                        />
                    </div>
                    {!selectedPatient && (
                        <div className="create-patient-container">
                            <button onClick={goToCreatePatient} className="create-patient-button">
                                <Icon  icon='bi:person-plus-fill' className="create-icon"/>
                                <span className="create-label">Crear paciente</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="right-column">
                <div className="results-container">
                    {!selectedPatient && (
                        <>
                            <div className="search-results-title">Resultados de búsqueda:</div>

                            {searchResults.length === 0 && (
                                <p className="results-message">{resultsFound}</p>
                            )}

                            <div className="cards-container">
                                {searchResults.map((p: any) => (
                                    <GeneralRecipeViewCard
                                        key={String(p?.medereentity ?? `${p?.document ?? "unknown"}-${p?.lastname ?? ""}`)}
                                        style={{ minWidth: '260px' }}
                                        title={<strong>{p.firstname + " " + p.lastname}</strong>}
                                        detail={`DNI: ${p.document}`}
                                        detail2={`Nac.: ${p.birthdate}`}
                                        onClick={() => handlePatientSelection(p)}
                                        backgroundColor='#EAEFEE'
                                        titleColor='#009688'
                                    />
                                ))}
                            </div>
                        </>
                    )}

                    {selectedPatient && (
                        <div className="selected-patient-block">
                            <p className="selected-patient-title">
                                Paciente seleccionado
                            </p>
                            <GeneralRecipeViewCard
                                style={{ minWidth: "300px" }}
                                title={<strong>{selectedPatient.firstname + " " + selectedPatient.lastname}</strong>}
                                detail={
                                    <div className="patient-affiliate">
                                    Afiliado:{" "}
                                    {(selectedPatient.insurances?.length ?? 0) > 1
                                        ? selectedCoverage?.benefitnumber ?? "-"
                                        : selectedPatient.insurances?.length === 1
                                        ? (selectedPatient.insurances[0] as any)?.benefitnumber ?? "-"
                                        : "-"}
                                    </div>
                                }
                                detail2={
                                    <>
                                    {(selectedPatient.insurances?.length ?? 0) > 1 ? (
                                        <div>
                                        <div className="patient-affiliate">Obra social</div>
                                        <Combobox
                                            className="border2"
                                            getItems={() => Promise.resolve(selectedPatient.insurances)}
                                            placeholder="Seleccionar obra social"
                                            optionLabel={"description"}
                                            value={selectedCoverage}
                                            setValue={(e: any) => setSelectedCoverage(e)}
                                        />
                                        </div>
                                    ) : selectedPatient.insurances?.length === 1 ? (
                                        <div className="patient-insurances">
                                        Obra social:{" "}
                                        {(selectedPatient.insurances[0] as any)?.description ??
                                            `${(selectedPatient.insurances[0] as any)?.healthentityplan?.healthentity
                                            ?.commercialname ?? "-"}, ${(selectedPatient.insurances[0] as any)?.healthentityplan?.name ?? "-"}`}
                                        </div>
                                    ) : (
                                        <div>Sin obra social</div>
                                    )}
                                    </>
                                }
                                detail3={`Cel.: ${selectedPatient.mobilephone ?? "-"}`}
                                onEdit={goToCreatePatient}
                                onRemove={removePatientSelection}
                                onClick={() => {}}
                                />
                        </div>
                    )}
                    <div className="ps-footer">
                    <Button
                        label="Continuar"
                        onClick={handleNext}
                        disabled={!isStepValid}
                        className="ps-btn-primary"
                    />
                    <Button
                        label="Cancelar"
                        onClick={handleBack}
                        className="ps-btn-secondary"
                    />
                    </div>
                </div>
            </div>
        </div>
    </>
    );
}
